import React, { useState } from "react";
import { addVariantToCart } from "@/lib/shopifyCart";

/**
 * TeamSetCard — single reusable card for a Full Team Set product.
 *
 * COMMERCE CONTRACT (Shopify is the source of truth)
 * --------------------------------------------------
 * Every value the customer sees or acts on comes from Shopify at
 * runtime — CollectorUp never carries hard-coded prices, inventory,
 * or line item math.
 *
 * BEHAVIOUR
 * ---------
 *   - "ADD TO CART" invokes `addVariantToCart(shopify_variant_id, 1)`.
 *   - Sold out → button disabled, reads "SOLD OUT".
 *   - No `shopify_variant_id` → "COMING SOON" state, button disabled.
 *   - Image + title + "View set" all navigate to `product_url`
 *     (Shopify product page) in the SAME tab so mobile back works.
 */
const AVAIL_LABEL = {
  IN_STOCK:  { label: "IN STOCK",  cls: "bg-emerald-100 text-emerald-800" },
  LOW_STOCK: { label: "LOW STOCK", cls: "bg-amber-100 text-amber-900" },
  SOLD_OUT:  { label: "SOLD OUT",  cls: "bg-neutral-200 text-neutral-700" },
};

const ACCENT = {
  gold:  "border-[#d4af37]/60 shadow-[6px_6px_0_#d4af37]",
  blue:  "border-[#1e4fb8]/60 shadow-[6px_6px_0_#1e4fb8]",
  white: "border-[var(--ink)]/25 shadow-[6px_6px_0_var(--ink)]",
};

export default function TeamSetCard({ product }) {
  const {
    country, country_code, variation = "white",
    product_title, image_url, price,
    availability = "IN_STOCK",
    shopify_variant_id,
    product_url,
    featured,
  } = product || {};

  const avail = AVAIL_LABEL[availability] || AVAIL_LABEL.IN_STOCK;
  const accent = ACCENT[variation] || ACCENT.white;
  const soldOut = availability === "SOLD_OUT";
  const canAdd = Boolean(shopify_variant_id) && !soldOut;

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onAdd = async () => {
    if (!canAdd || busy) return;
    setBusy(true);
    setErr("");
    const res = await addVariantToCart(shopify_variant_id, 1);
    setBusy(false);
    if (!res.ok) setErr("Could not open cart — please refresh.");
  };

  let ctaLabel;
  if (!shopify_variant_id) ctaLabel = "COMING SOON";
  else if (soldOut) ctaLabel = "SOLD OUT";
  else if (busy) ctaLabel = "ADDING…";
  else ctaLabel = "ADD TO CART";

  const displayTitle = country || product_title;
  // Same-tab navigation (no target="_blank") — matches the shop UX spec
  // so mobile back-button returns to the catalog naturally.
  const linkProps = product_url ? { href: product_url } : {};

  const Image = (
    <div className="aspect-square bg-neutral-50 flex items-center justify-center overflow-hidden">
      {image_url ? (
        <img src={image_url} alt={product_title || country}
             loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <div className="text-6xl" aria-hidden>{country_code ? "🏳️" : "📦"}</div>
      )}
    </div>
  );

  return (
    <div
      data-testid={`team-set-card-${variation}-${country_code || "unknown"}`}
      data-featured={featured ? "true" : "false"}
      className={`rounded-2xl border-2 bg-white overflow-hidden flex flex-col ${accent}`}
    >
      {product_url ? (
        <a {...linkProps}
           data-testid={`team-set-card-image-${variation}-${country_code || "x"}`}
           aria-label={displayTitle}
           className="block">
          {Image}
        </a>
      ) : Image}

      <div className="p-3 md:p-3.5 flex flex-col gap-1.5 flex-1">
        <div className="flex items-center justify-between gap-2">
          {product_url ? (
            <a {...linkProps}
               className="font-display text-base md:text-lg leading-tight truncate hover:underline"
               data-testid={`team-set-card-title-${variation}-${country_code || "x"}`}>
              {displayTitle}
            </a>
          ) : (
            <div className="font-display text-base md:text-lg leading-tight truncate">
              {displayTitle}
            </div>
          )}
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${avail.cls}`}>{avail.label}</span>
        </div>
        <div className="text-[10px] md:text-xs text-[var(--smoke)] uppercase tracking-wide leading-snug">
          {variation === "gold" ? "Gold Crumple" : variation === "blue" ? "Blue Parallel" : "White Original"} · 20-sticker set
        </div>
        <div className="text-lg md:text-xl font-display leading-none mt-0.5">
          {price ? price : <span className="text-[var(--smoke)] text-sm">Price from Shopify</span>}
        </div>

        <div className="mt-auto flex flex-col gap-0.5 pt-1">
          <button
            type="button"
            onClick={onAdd}
            disabled={!canAdd || busy}
            aria-disabled={!canAdd || busy}
            className={`btn-yellow btn-yellow--slim w-full justify-center ${
              !canAdd || busy ? "opacity-60 cursor-not-allowed" : ""
            }`}
            data-testid={`team-set-card-cta-${variation}-${country_code || "x"}`}
            data-variant-id={shopify_variant_id || ""}
          >
            {ctaLabel}
          </button>

          {product_url ? (
            <a
              {...linkProps}
              className="text-[11px] text-[var(--smoke)] hover:text-[var(--ink)] text-center underline underline-offset-2"
              data-testid={`team-set-card-view-${variation}-${country_code || "x"}`}
            >
              View set
            </a>
          ) : null}

          {err ? (
            <div
              role="alert"
              className="text-[11px] text-red-700"
              data-testid={`team-set-card-error-${variation}-${country_code || "x"}`}
            >
              {err}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
